import React, { useEffect, useState } from "react";
import MessageBar, {
  config,
  errorNotify,
  getValueFromLocalStorage,
  localStorageKey,
} from "../../../utils/helper";
import UserRequestCard from "../../../Components/UserRequestCard/UserRequestCard";
import { authorizedGet } from "../../../Config/networkWithToken";
import configuration from "../../../Config/configuration";
import Spinner from "../../../Components/Spinner/Spinner";
import Strings from "../../../utils/Strings";
import { ImCancelCircle } from "react-icons/im";
import AdvanceSearch from "../../../Components/AdvanceSearch/AdvanceSearch";
import { IoIosSearch } from "react-icons/io";
import { useQuery, useQueryClient } from "@tanstack/react-query";

export const QUESTIONNAIRE_REQUESTS_KEY = "questionnaireRequests";

const QuestionnaireRequests = () => {
  const adminId = getValueFromLocalStorage(localStorageKey.USERDATA, "adminId");
  const queryClient = useQueryClient();

  const [activeTab, setActiveTab] = useState(0);
  const [filter, setFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [showAdvanceSearch, setShowAdvanceSearch] = useState(false);
  const [searchData, setSearchData] = useState([]);
  const [userRequests, setUserRequests] = useState([]);
  const [acceptedRequest, setAcceptedRequest] = useState([]);
  const [rejectedRequest, setRejectedRequest] = useState([]);
  const [mutuallyAcceptedRequest, setMutuallyAcceptedRequest] = useState([]);
  const [adminMutuallyReject, setAdminMutuallyReject] = useState([]);
  const [pendingInTalkRequest, setPendingInTalkRequest] = useState([]);

  const { isLoading: loading, data: fetchedData } = useQuery({
    queryKey: [QUESTIONNAIRE_REQUESTS_KEY, adminId],
    queryFn: async () => {
      const res = await authorizedGet(
        `${configuration.apis.getAllQuestionnaireRequests}?adminId=${adminId}`,
        config,
      );
      if (res?.data?.status) {
        return res.data.requests;
      } else {
        errorNotify(Strings.ERROR);
        return [];
      }
    },
    enabled: !!adminId,
    staleTime: 0,
    retry: false,
  });

  useEffect(() => {
    if (!fetchedData) return;
    processAndSetData(fetchedData);
  }, [fetchedData]);

  const processAndSetData = (data) => {
    const users = [];
    const accepted = [];
    const rejected = [];
    const mutuallyAccepted = [];
    const mutuallyAdminReject = [];
    const pendingInTalk = [];

    data.forEach((value) => {
     
      if (value?.inTalk === true) {
        pendingInTalk.push(value);
      } else if (value?.adminApproval === "pending") {
        users.push(value);
      } else if (value?.overAllAdminDecision === "rejected") {
        mutuallyAdminReject.push(value);
      } else if (value?.adminApproval === "approved") {
        if (value?.userApproval === "approved") {
          const alreadyFixedOrMarried =
            value?.markedMarried ||
            value?.isUserMarried ||
            value?.isReqUserMarried;
          if (!alreadyFixedOrMarried) {
            mutuallyAccepted.push(value);
          }
        } else {
          accepted.push(value);
        }
      } else if (value?.adminApproval === "rejected") {
        rejected.push(value);
      }
    });

    setUserRequests(users);
    setAcceptedRequest(accepted);
    setRejectedRequest(rejected);
    setMutuallyAcceptedRequest(mutuallyAccepted);
    setAdminMutuallyReject(mutuallyAdminReject);
    setPendingInTalkRequest(pendingInTalk);
  };

  const refetchRequests = () => {
    queryClient.refetchQueries({
      queryKey: [QUESTIONNAIRE_REQUESTS_KEY, adminId],
    });
  };

  const mutualUniqueCount = new Set(
    mutuallyAcceptedRequest.map((item) => {
      const ids = [item.user?._id, item.requestedUser?._id].sort();
      return ids.join("-");
    }),
  ).size;

  const tabList = [
    { name: "User Requests", count: userRequests.length },
    { name: "Accepted Requests", count: acceptedRequest.length },
    { name: "Denied Requests", count: rejectedRequest.length },
    { name: "Mutually Accepted Requests", count: mutualUniqueCount },
    { name: "Mutually Admin Declined", count: adminMutuallyReject.length },
    { name: "Pending In Talk With User", count: pendingInTalkRequest.length },
  ];

  const handleTabClick = (index) => {
    setActiveTab(index);
    if (showAdvanceSearch) {
setSearchQuery("");
      setSearchData([]);
      processAndSetData(fetchedData);
    }
  };

  const handleAdvanceSearch = (searchValue) => {
    const query = searchValue.toLowerCase().trim();
    setSearchQuery(query);

    const filterFn = (list) =>
      list.filter((user) => {
        const userName = user?.user?.name?.toLowerCase() || "";
        const requestedUserName =
          user?.requestedUser?.name?.toLowerCase() || "";
        return userName.includes(query) || requestedUserName.includes(query);
      });

    const tabSources = [
      userRequests,
      acceptedRequest,
      rejectedRequest,
      mutuallyAcceptedRequest,
      adminMutuallyReject,
      pendingInTalkRequest,
    ];

    const search = filterFn(tabSources[activeTab] || []);
    setSearchData(search);

    const setters = [
      setUserRequests,
      setAcceptedRequest,
      setRejectedRequest,
      setMutuallyAcceptedRequest,
      setAdminMutuallyReject,
      setPendingInTalkRequest,
    ];
    setters[activeTab](search);
  };

  const toggleSearchBar = () => {
    setShowAdvanceSearch((prev) => !prev);
    setSearchQuery("");
    setSearchData([]);
    if (showAdvanceSearch) {
      processAndSetData(fetchedData);
    }
  };

  const filterRequests = (value) => {
    const today = new Date();
    setFilter(value);

    let data = [];
    if (searchData.length > 0) {
      data = searchData;
    } else if (searchQuery && searchData.length === 0) {
      data = [];
    } else {
      data = fetchedData;
    }

    if (value === "all") {
      processAndSetData(data);
      return;
    }

    const currentYear = today.getFullYear();

    const filtered = data.filter((user) => {
      if (!user?.sentAt) return false;

      const requestDate = new Date(user.sentAt);
      const diffDays =
        (today.getTime() - requestDate.getTime()) / (1000 * 60 * 60 * 24);
      const month = requestDate.getMonth();
      const year = requestDate.getFullYear();

      if (value === "today")
        return requestDate.toDateString() === today.toDateString();
      if (value === "week") return diffDays < 7 && diffDays >= 0;
      if (value === "month")
        return month === today.getMonth() && year === today.getFullYear();
      if (value === "year") return year === today.getFullYear();
      if (value === "q1") return month <= 2 && year === currentYear;
      if (value === "q2")
        return month >= 3 && month <= 5 && year === currentYear;
      if (value === "q3")
        return month >= 6 && month <= 8 && year === currentYear;
      if (value === "q4") return month >= 9 && year === currentYear;
      if (value === "h1") return month <= 5 && year === currentYear;
      if (value === "h2") return month >= 6 && year === currentYear;

      return false;
    });

    const sorted = [...filtered].sort(
      (a, b) => new Date(b.sentAt) - new Date(a.sentAt),
    );

    processAndSetData(sorted);
  };

  const tabDataMap = [
    userRequests,
    acceptedRequest,
    rejectedRequest,
    mutuallyAcceptedRequest,
    adminMutuallyReject,
    pendingInTalkRequest,
  ];

  const emptyMessages = [
    "No Questionnaire requests available.",
    "No Accepted requests available.",
    "No Declined requests available.",
    "No Mutually Accepted requests available.",
    "No mutually accepted requests have been declined by the admin.",
    "No Pending In Talk requests available.",
  ];

  return (
    <div className="font-jost relative ml-4 h-auto rounded-lg p-2">
      <div className="flex w-full items-center justify-center text-xs text-gray-400 md:justify-end">
        Note: The details are sorted by the date the request was sent.
      </div>

      <div className="flex w-full flex-col justify-between border-b border-gray-300 md:flex-row">
        <ul className="flex flex-col justify-center md:flex-row">
          {tabList.map((list, index) => (
            <li
              key={index}
              className={`mt-3 cursor-pointer px-4 py-2 text-center text-textGreen transition-all ${
                showAdvanceSearch
                  ? "text-[13px] xl:text-[16px]"
                  : "sm:text-xs md:text-base"
              } ${
                activeTab === index
                  ? "border-b-2 border-textYellow text-textYellow"
                  : "hover:bg-gray-100 hover:text-textYellow"
              }`}
              onClick={() => handleTabClick(index)}
            >
              {list.name}({list.count})
            </li>
          ))}
        </ul>

        <div className="flex flex-wrap items-center justify-center">
          <div className="flex flex-row items-center gap-2">
            <div className="flex items-center rounded-lg">
              {!showAdvanceSearch ? (
                <IoIosSearch
                  className="cursor-pointer text-xl text-gray-500 sm:text-2xl"
                  onClick={toggleSearchBar}
                />
              ) : (
                <div className="flex items-center gap-2">
                  <AdvanceSearch onSearchValue={handleAdvanceSearch} />
                  <ImCancelCircle
                    className="cursor-pointer text-lg text-gray-500 sm:text-xl"
                    onClick={toggleSearchBar}
                  />
                </div>
              )}
            </div>
            <div className="flex flex-col items-center">
              <p className="text-xs">Sort by:</p>
              <select
                className="rounded-md border px-1 py-1 text-xs"
                value={filter}
                onChange={(e) => filterRequests(e.target.value)}
              >
                <option value="all">All</option>
                <option value="today">Today</option>
                <option value="week">This Week</option>
                <option value="month">This Month</option>
                <option value="year">This Year</option>
                <option value="q1">Q1 (Jan - Mar)</option>
                <option value="q2">Q2 (Apr - Jun)</option>
                <option value="q3">Q3 (Jul - Sep)</option>
                <option value="q4">Q4 (Oct - Dec)</option>
                <option value="h1">H1 (Jan - Jun)</option>
                <option value="h2">H2 (Jul - Dec)</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      {tabDataMap.map((tabData, index) =>
        activeTab === index ? (
          <div key={index}>
            {loading ? (
              <div className="flex min-h-screen flex-col items-center justify-center">
                <Spinner />
              </div>
            ) : tabData.length > 0 ? (
              <div className="flex flex-wrap gap-4 p-2">
                {tabData.map((request) => (
                  <UserRequestCard
                    key={request?._id}
                    user={request?.user}
                    requestedUser={request?.requestedUser}
                    activeTab={activeTab}
                    reqId={request?._id}
                    sentAt={request?.sentAt}
                    approvalAdminId={request?.approvalAdminId}
                    adminApprovedAt={request?.adminApprovedAt}
                    comments={request?.comments}
                    userApprovalAt={request?.userApprovalAt}
                    overAllAdminDecision={request?.overAllAdminDecision}
                    overAllAdminDecisionAt={request?.overAllAdminDecisionAt}
                    overAllAdmincomments={request?.overAllAdmincomments}
                    overAllAdminId={request?.overAllAdminId}
                    onActionSuccess={refetchRequests}
                    setUserRequests={setUserRequests}
                    setAcceptedRequest={setAcceptedRequest}
                    setRejectedRequest={setRejectedRequest}
                    setMutuallyAcceptedRequest={setMutuallyAcceptedRequest}
                    setAdminMutuallyReject={setAdminMutuallyReject}
                    setPendingInTalkRequest={setPendingInTalkRequest}
                    inTalkFromTab={request?.inTalkFromTab}
                  />
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center p-2">
                <h4 className="text-black">{emptyMessages[index]}</h4>
              </div>
            )}
          </div>
        ) : null,
      )}

      <MessageBar />
    </div>
  );
};

export default QuestionnaireRequests;
